import React from 'react';
import { Info, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/shadcn';
import { cn } from '../../../utils';
import type { DashboardKpi } from '../../../types';
import { getMetricDef, MetricId } from './metricDefs';
import { metricTone, toneStyle } from './metricTones';

export interface MetricStripItem {
  metric: MetricId;
  kpi?: DashboardKpi;
  format?: 'currency' | 'number' | 'percent';
  /** A fall is the good outcome (payouts, debt, failure rates). */
  invertDelta?: boolean;
  /** Renders larger — use for the one number that leads the group. */
  emphasis?: boolean;
}

interface MetricStripProps {
  items: MetricStripItem[];
  /** Small caption above the strip. */
  title?: string;
  /** Items per row at the xl breakpoint. Defaults to one row for the whole set. */
  columnsClassName?: string;
  className?: string;
}

function formatValue(value: number, format: MetricStripItem['format']): string {
  if (!Number.isFinite(value)) return '—';
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      // Whole dollars past four figures: cents are noise at strip density.
      maximumFractionDigits: Math.abs(value) >= 10000 ? 0 : 2,
    }).format(value);
  }
  if (format === 'percent') {
    return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

const Delta: React.FC<{ deltaPct?: number | null; invert: boolean }> = ({ deltaPct, invert }) => {
  if (deltaPct === undefined) return null;
  if (deltaPct === null) {
    return <span className="text-[10px] font-medium text-muted-foreground">N/A</span>;
  }
  const isFlat = Math.abs(deltaPct) < 0.05;
  const isImprovement = invert ? deltaPct < 0 : deltaPct > 0;
  const Icon = isFlat ? Minus : deltaPct > 0 ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-[10px] font-semibold',
        isFlat ? 'text-muted-foreground' : isImprovement ? 'text-emerald-600' : 'text-red-600',
      )}
    >
      <Icon className="h-2.5 w-2.5" />
      {deltaPct > 0 ? '+' : ''}
      {deltaPct.toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  );
};

/**
 * A row of KPIs inside one card, separated by hairlines.
 *
 * Replaces a grid of individual stat cards. Same information, roughly a third
 * of the vertical space — which is the whole point: the charts have to be
 * visible when the tab opens, not below a screenful of numbers. Tone survives
 * as a colour bar on each cell rather than a tinted card, so the semantic
 * palette (money-out orange, risk rose, credits amber) still reads.
 */
const MetricStrip: React.FC<MetricStripProps> = ({
  items,
  title,
  columnsClassName,
  className,
}) => {
  const columns =
    columnsClassName ||
    // Sensible default: two up on mobile, spreading out with width.
    'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

  return (
    <div className={className}>
      {title && (
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-blue-900/70">
          {title}
        </p>
      )}
      <Card className="overflow-hidden border-blue-100 bg-white">
        <div className={cn('grid', columns)}>
          {items.map((item) => {
            const def = getMetricDef(item.metric);
            const tone = toneStyle(metricTone(item.metric));
            const value = item.kpi != null ? formatValue(item.kpi.value, item.format) : '—';
            return (
              <div
                key={item.metric}
                className="flex min-w-0 items-stretch gap-2 border-b border-r border-blue-100/70 px-3 py-2 last:border-r-0"
              >
                <span className={cn('w-0.5 shrink-0 rounded-full', tone.chipBg)} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-start gap-1">
                    {/* Wraps rather than truncates: a clipped label like
                        "COUNTER DEPOSITS (CREDI…" is unreadable, and the row
                        only grows by one line. */}
                    <span className="text-[10px] font-medium uppercase leading-tight tracking-wide text-muted-foreground">
                      {def.label}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={`What is ${def.label}?`}
                          className="mt-px shrink-0 text-blue-300 hover:text-blue-700"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {def.description}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-1.5">
                    <span
                      className={cn(
                        'truncate font-semibold tabular-nums',
                        item.emphasis ? 'text-lg' : 'text-sm',
                        tone.value,
                      )}
                    >
                      {value}
                    </span>
                    <Delta deltaPct={item.kpi?.deltaPct} invert={item.invertDelta ?? false} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default MetricStrip;
